import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Spin } from "antd";
import { XIcon, Asterisk } from "../../../../assets/icons";
import * as Yup from "yup";
import { Formik } from "formik";
import AuthService from "../../../../base/services/authentication.service";
import { httpService } from "../../../../base/services/httpService.service";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./filters.scss";
import { useNotification } from "../../../../base/hooks/useNotification";
import { useTranslation } from "../../../../i18n/LanguageContext";

const EditRepresentativeDetailsModal = ({
   visible,
   onCancel,
   onOk,
   id,
   fetchData,
   updateOrganization,
   representativeId
}) => {
   const { t } = useTranslation();
   const [formData, setFormData] = useState({
      name: "",
      location: "",
      phone: "",
      email: ""
   });
   const organizationId = AuthService.getUser()?.organizationId;
   const [loading, setLoading] = useState(false);
   const { api: notificationApi, notificationContextHolder } = useNotification();

   useEffect(() => {
      if (visible && representativeId) {
         fetchRepresentativeDetails(representativeId);
      }
   }, [visible, representativeId]);

   const fetchRepresentativeDetails = async (representativeId) => {
      try {
         httpService.get(`/Organizations/representative/${representativeId}`, (res) => {
            const representativeData = res.data;
            setFormData({
               name: representativeData.name || "",
               location: representativeData.location || "",
               phone: representativeData.phone || "",
               email: representativeData.email || ""
            });
         });
      } catch (error) {
         console.error("Error fetching representative details:", error);
      }
   };

   const validationSchema = Yup.object({
      name: Yup.string().required(t("name-required-validation")),
      phone: Yup.string()
         .nullable()
         .test("is-valid-phone-number", t("invalid-phone-number-validation"), (value) => {
            if (!value) return false;
            return value.startsWith("+") && value.length > 7;
         })
         .required(t("phone-number-required-validation")),
      email: Yup.string()
         .email(t("invalid-email-validation"))
         .required(t("email-required-validation"))
   });

   const handleSubmit = async (values) => {
      setLoading(true);
      try {
         const response = await httpService.put(`/Organizations/${organizationId}/representative`, {
            id: representativeId,
            name: values.name,
            location: values.location,
            phone: values.phone,
            email: values.email
         });
         onOk(representativeId);
         fetchData(representativeId, id);
         httpService.get(`/Organizations/${id}`, (res) => {
            updateOrganization(res.data);
         });
         notificationApi.success("team-member", {
            message: t("representative-updated-success-title"),
            description: t("representative-updated-success-description")
         });
         setLoading(false);
      } catch (error) {
         notificationApi.error("team-member", {
            message: t("toast-oops-title"),
            description: t("representative-update-error-description")
         });
         setLoading(false);
      }
   };

   return (
      <>
         {notificationContextHolder}

         <Modal
            open={visible}
            onCancel={onCancel}
            onOk={handleSubmit}
            closable={false}
            footer={[]}
            destroyOnClose
         >
            <Formik
               initialValues={formData}
               validationSchema={validationSchema}
               onSubmit={handleSubmit}
               enableReinitialize
            >
               {({ errors, values, handleSubmit, handleChange, setFieldValue }) => (
                  <form onSubmit={handleSubmit}>
                     <div>
                        <img
                           src={XIcon}
                           alt="XIcon"
                           onClick={onCancel}
                           style={{ float: "right", cursor: "pointer" }}
                        />
                        <div className="add-representative-details">
                           <h1>{t("edit-representative-detail-title")}</h1>
                        </div>
                        <div>
                           <div className="label-name-add-representative-details">
                              <img src={Asterisk} alt="Asterisk" />
                              <label>{t("full-name-label")}</label>
                           </div>
                           <Input
                              autoComplete="off"
                              value={values.name}
                              type="text"
                              name="name"
                              onChange={handleChange}
                              placeholder={t("name")}
                           />
                        </div>
                        <div className="error-message">{errors.name}</div>
                        <div>
                           <div className="label-name-add-representative-details">
                              <img src={Asterisk} alt="Asterisk" />
                              <label>{t("email")}</label>
                           </div>
                           <Input
                              autoComplete="off"
                              value={values.email}
                              type="email"
                              name="email"
                              onChange={handleChange}
                              placeholder={t("email")}
                           />
                        </div>
                        <div className="error-message">{errors.email}</div>
                        <div>
                           <div className="label-name-add-representative-details">
                              <img src={Asterisk} alt="Asterisk" />
                              <label>{t("phone-label")}</label>
                           </div>
                           <PhoneInput
                              international
                              defaultCountry="US"
                              value={values.phone}
                              onChange={(value) => setFieldValue("phone", value)}
                              className="custom-phone-input"
                           />
                        </div>
                        <div className="error-message">{errors.phone}</div>
                        <div>
                           <div className="label-name-add-representative-details">
                              <label>{t("location-label")}</label>
                           </div>
                           <Input
                              autoComplete="off"
                              value={values.location}
                              type="text"
                              onChange={handleChange}
                              name="location"
                              placeholder={t("location-label")}
                           />
                        </div>
                        <div className="footer-buttons-add-representative-details" key="uniqueKey">
                           <Button className="cencel-delete-button" key="cancel" onClick={onCancel}>
                              {t("cancel")}
                           </Button>
                           <Button className="delete-button" type="submit" onClick={handleSubmit}>
                              {loading ? <Spin /> : t("edit-representative-button")}
                           </Button>
                        </div>
                     </div>
                  </form>
               )}
            </Formik>
         </Modal>
      </>
   );
};

export default EditRepresentativeDetailsModal;
